/**
 * Player de música da mesa (system/musicPlayer; TfrmMusicPlayerModule): a faixa
 * tocando, a barra de progresso, os controles e a playlist.
 *
 * Os mestres montam a playlist (arquivos enviados para a sala ou links) e
 * controlam a reprodução; todo mundo ouve e ajusta o próprio volume. O som em si
 * sai do AudioEngine, que vive na mesa.
 */
import { useRef, useState } from 'react';
import { uploadRoomFile } from '../../files.js';
import { hasFileDrag, readFileDrag } from '../../cloud/fileDrag.js';
import { nameOf } from '../../cloud/inRoom.js';
import { useT } from '../../i18n/index.js';
import type { AudioRepeat } from '../../shared.js';
import { useApp } from '../../store.js';
import { Icon } from '../../ui/icons.js';
import { askText } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { unlockAudio, usePlayback } from './AudioEngine.js';
import './audio.css';

export function useIsRoomGm(): boolean {
  return useApp((s) => !!s.user && !!s.room && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm')));
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** Envia arquivos de áudio para a sala e devolve as faixas prontas para a fila. */
export async function uploadAudioFiles(files: FileList | File[]): Promise<{ url: string; title: string }[]> {
  const out: { url: string; title: string }[] = [];
  for (const file of [...files]) {
    if (!file.type.startsWith('audio/') && !/\.(mp3|ogg|oga|wav|m4a|aac|flac|webm|opus)$/i.test(file.name)) continue;
    const stored = await uploadRoomFile(file, 'audio');
    out.push({ url: `file:${stored.id}`, title: file.name.replace(/\.[^.]+$/, '').slice(0, 120) || file.name });
  }
  return out;
}

/** O título que o link sugere: o nome do arquivo, sem a extensão (um "%" solto fica como veio). */
export function linkTitle(url: string): string {
  const last = url.split(/[?#]/)[0]!.split('/').pop() ?? '';
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    // "%" sem os dois dígitos: o decodeURIComponent lança, e o link nem chegava a pedir o título
  }
  return name.replace(/\.[^.]+$/, '');
}

/** Pergunta um link http(s) e um título. */
export async function askAudioLink(t: (k: string) => string): Promise<{ url: string; title: string } | null> {
  const url = await askText({ title: t('audio.link.title'), label: t('audio.link.url'), initial: 'https://', maxLength: 2000, action: t('audio.link.next') });
  if (!url) return null;
  if (!/^https?:\/\/\S+$/.test(url)) {
    toast(t('audio.link.invalid'), 'error');
    return null;
  }
  const guess = linkTitle(url);
  const title = await askText({ title: t('audio.link.title'), label: t('audio.link.name'), initial: guess.slice(0, 120) || t('audio.link.defaultName'), maxLength: 120, action: t('audio.link.add') });
  return title ? { url, title } : null;
}

export function MusicPlayerModule() {
  const t = useT();
  const audio = useApp((s) => s.audio);
  const settings = useApp((s) => s.settings);
  const updateSettings = useApp((s) => s.updateSettings);
  const control = useApp((s) => s.controlAudio);
  const enqueue = useApp((s) => s.enqueueAudio);
  const remove = useApp((s) => s.removeAudio);
  const setRoomVolume = useApp((s) => s.setAudioVolume);
  const setRepeat = useApp((s) => s.setAudioRepeat);
  const playback = usePlayback();
  const isGm = useIsRoomGm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [seeking, setSeeking] = useState<number | null>(null);

  const current = audio?.current ?? null;
  const playlist = audio?.playlist ?? [];
  const playing = !!audio?.isPlaying;
  const repeat = audio?.repeat ?? 'all';
  const duration = playback.duration;
  const position = seeking ?? Math.min(playback.position, duration || playback.position);

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const tracks = await uploadAudioFiles(files);
      if (!tracks.length) toast(t('audio.notAudio'), 'error');
      for (const tr of tracks) enqueue(tr.url, tr.title, 'music');
    } catch (error) {
      toast(t('audio.uploadFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      setBusy(false);
    }
  };

  const nextRepeat: Record<AudioRepeat, AudioRepeat> = { all: 'one', one: 'off', off: 'all' };

  return (
    <div
      className="rd-music"
      onDragOver={(e) => {
        if (isGm && (e.dataTransfer.types.includes('Files') || hasFileDrag(e.dataTransfer))) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!isGm) return;
        e.preventDefault();
        // um áudio do Desk Cloud (o painel Arquivos da mesa) entra na playlist sem enviar de novo
        const cloudFile = readFileDrag(e.dataTransfer);
        if (cloudFile) {
          if (cloudFile.kind === 'audio' || cloudFile.mime.startsWith('audio/')) enqueue(`file:${cloudFile.id}`, nameOf(cloudFile.name));
          else toast(t('audio.notAudio'), 'error');
          return;
        }
        void addFiles(e.dataTransfer.files);
      }}
    >
      {playback.blocked ? (
        <button type="button" className="rd-audioBanner" onClick={unlockAudio}>
          <Icon name="volume" size={16} /> {t('audio.blocked')}
        </button>
      ) : null}

      <section className="rd-music__now" aria-label={t('audio.nowPlaying')}>
        <div className={`rd-music__art${playing ? ' is-playing' : ''}`} aria-hidden="true">
          <Icon name="music" size={30} strokeWidth={1.6} />
        </div>
        <div className="rd-music__meta">
          <div className="rd-music__label">{t('audio.nowPlaying')}</div>
          <div className="rd-music__title" title={current?.title}>
            {current?.title ?? t('audio.nothing')}
          </div>
          {playback.failed && current ? <div className="rd-music__error">{t('audio.failed')}</div> : null}
        </div>
      </section>

      <div className="rd-music__progress">
        <span className="rd-music__time">{formatTime(position)}</span>
        <input
          className="rd-range rd-music__seek"
          type="range"
          min={0}
          max={duration || 1}
          step={0.5}
          value={duration ? position : 0}
          disabled={!isGm || !current || !duration}
          aria-label={t('audio.seek')}
          onChange={(e) => setSeeking(Number(e.target.value))}
          onPointerUp={() => {
            if (seeking !== null) control('seek', { positionSeconds: seeking });
            setSeeking(null);
          }}
          onKeyUp={() => {
            if (seeking !== null) control('seek', { positionSeconds: seeking });
            setSeeking(null);
          }}
        />
        <span className="rd-music__time">{formatTime(duration)}</span>
      </div>

      {isGm ? (
        <div className="rd-music__controls" role="group" aria-label={t('audio.controls')}>
          <button type="button" className="rd-roundBtn" title={t('audio.prev')} aria-label={t('audio.prev')} disabled={!playlist.length} onClick={() => control('prev')}>
            <Icon name="prev" size={18} filled />
          </button>
          <button
            type="button"
            className="rd-roundBtn rd-roundBtn--main"
            title={playing ? t('audio.pause') : t('audio.play')}
            aria-label={playing ? t('audio.pause') : t('audio.play')}
            disabled={!current}
            onClick={() => {
              unlockAudio();
              control(playing ? 'pause' : 'play');
            }}
          >
            <Icon name={playing ? 'pause' : 'play'} size={22} filled strokeWidth={1} />
          </button>
          <button type="button" className="rd-roundBtn" title={t('audio.next')} aria-label={t('audio.next')} disabled={!playlist.length} onClick={() => control('next')}>
            <Icon name="next" size={18} filled />
          </button>
          <button type="button" className="rd-roundBtn" title={t('audio.stop')} aria-label={t('audio.stop')} disabled={!current} onClick={() => control('stop')}>
            <Icon name="stop" size={16} filled />
          </button>
          <button
            type="button"
            className={`rd-roundBtn rd-music__repeat${repeat !== 'off' ? ' is-on' : ''}`}
            title={t(`audio.repeat.${repeat}`)}
            aria-label={t(`audio.repeat.${repeat}`)}
            onClick={() => setRepeat(nextRepeat[repeat])}
          >
            <Icon name="repeat" size={18} />
            {repeat === 'one' ? <span className="rd-music__repeatOne">1</span> : null}
          </button>
        </div>
      ) : (
        <p className="rd-music__note">{t('audio.gmControls')}</p>
      )}

      <div className="rd-music__volumes">
        <VolumeRow
          label={t('audio.myVolume')}
          value={settings.musicVolume}
          muted={settings.audioMuted}
          onMute={() => updateSettings({ audioMuted: !settings.audioMuted })}
          onChange={(v) => updateSettings({ musicVolume: v, audioMuted: false })}
        />
        {isGm ? <VolumeRow label={t('audio.roomVolume')} value={audio?.volume ?? 1} onChange={(v) => setRoomVolume(v)} commitOnRelease /> : null}
      </div>

      <section className="rd-music__list" aria-label={t('audio.playlist')}>
        <header className="rd-music__listHead">
          <h3>
            {t('audio.playlist')} <span>({playlist.length})</span>
          </h3>
          {isGm ? (
            <div className="rd-music__add">
              <button type="button" className="rd-iconBtn" title={t('audio.addFiles')} aria-label={t('audio.addFiles')} disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? <span className="rd-spinner" /> : <Icon name="upload" size={18} />}
              </button>
              <button
                type="button"
                className="rd-iconBtn"
                title={t('audio.addLink')}
                aria-label={t('audio.addLink')}
                onClick={async () => {
                  const tr = await askAudioLink(t);
                  if (tr) enqueue(tr.url, tr.title, 'music');
                }}
              >
                <Icon name="link" size={18} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="audio/*"
                multiple
                hidden
                onChange={(e) => {
                  const files = e.target.files;
                  void addFiles(files).finally(() => (e.target.value = ''));
                }}
              />
            </div>
          ) : null}
        </header>
        {playlist.length ? (
          <ol className="rd-music__tracks">
            {playlist.map((tr, i) => {
              const isCurrent = current?.id === tr.id;
              return (
                <li key={tr.id} className={`rd-music__track${isCurrent ? ' is-current' : ''}`}>
                  <button
                    type="button"
                    className="rd-music__trackMain"
                    disabled={!isGm}
                    title={isGm ? t('audio.playThis') : tr.title}
                    onClick={() => {
                      unlockAudio();
                      control('select', { trackId: tr.id });
                    }}
                  >
                    <span className="rd-music__trackNo">{isCurrent && playing ? <Icon name="volume" size={14} /> : i + 1}</span>
                    <span className="rd-music__trackTitle">{tr.title}</span>
                    <span className="rd-music__trackBy">{tr.addedByName}</span>
                  </button>
                  {isGm ? (
                    <button type="button" className="rd-iconBtn rd-music__remove" title={t('audio.remove')} aria-label={t('audio.removeNamed', { name: tr.title })} onClick={() => remove(tr.id)}>
                      <Icon name="close" size={14} />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="rd-music__empty">{isGm ? t('audio.empty.gm') : t('audio.empty')}</p>
        )}
      </section>
    </div>
  );
}

export function VolumeRow({
  label,
  value,
  onChange,
  muted,
  onMute,
  commitOnRelease,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  muted?: boolean;
  onMute?: () => void;
  /** só manda ao soltar (volume da mesa vai pela rede) */
  commitOnRelease?: boolean;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  const shown = draft ?? value;
  const commit = () => {
    if (draft !== null) onChange(draft);
    setDraft(null);
  };
  return (
    <label className="rd-volume">
      {onMute ? (
        <button type="button" className="rd-iconBtn" aria-label={label} aria-pressed={muted} onClick={onMute}>
          <Icon name={muted || shown === 0 ? 'mute' : 'volume'} size={18} />
        </button>
      ) : (
        <span className="rd-volume__icon">
          <Icon name="volume" size={18} />
        </span>
      )}
      <span className="rd-volume__label">{label}</span>
      <input
        className="rd-range"
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={muted ? 0 : shown}
        aria-label={label}
        onChange={(e) => (commitOnRelease ? setDraft(Number(e.target.value)) : onChange(Number(e.target.value)))}
        onPointerUp={commit}
        onKeyUp={commit}
      />
      <span className="rd-volume__value">{Math.round((muted ? 0 : shown) * 100)}%</span>
    </label>
  );
}
